import React, { useState, useEffect, useRef, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSignupEnabled } from '../api/generalApi';

interface FormData {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, completePhone, forgotPassword, error, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<FormData>({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [formError, setFormError] = useState<string>('');
  const [formSuccess, setFormSuccess] = useState<string>('');

  // Phone completion state
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [phone, setPhone] = useState<string>('');
  const [phoneFirstName, setPhoneFirstName] = useState<string>('');
  const [phoneLastName, setPhoneLastName] = useState<string>('');

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // const [googleReady, setGoogleReady] = useState(false);
  const googleInitializedRef = useRef(false);
  const loginWithGoogleRef = useRef(loginWithGoogle);
  useEffect(() => { loginWithGoogleRef.current = loginWithGoogle; }, [loginWithGoogle]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    getSignupEnabled().then(setSignupEnabled).catch(() => {});
  }, []);

  // // Load Google Identity Services script
  // useEffect(() => {
  //   const scriptId = 'google-identity-script';
  //   if ((window as any).google?.accounts?.id) {
  //     setGoogleReady(true);
  //     return;
  //   }
  //   if (!document.getElementById(scriptId)) {
  //     const script = document.createElement('script');
  //     script.id = scriptId;
  //     script.src = 'https://accounts.google.com/gsi/client?hl=he';
  //     script.async = true;
  //     script.defer = true;
  //     script.onload = () => setGoogleReady(true);
  //     document.body.appendChild(script);
  //   } else {
  //     const interval = setInterval(() => {
  //       if ((window as any).google?.accounts?.id) {
  //         setGoogleReady(true);
  //         clearInterval(interval);
  //       }
  //     }, 100);
  //     return () => clearInterval(interval);
  //   }
  // }, []);

  // Initialize Google once, then render button whenever the div is visible
  useEffect(() => {
    if (pendingToken || showForgot) return;
  
    const g = (window as any).google;
    if (!g?.accounts?.id) return;
  
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
  
    if (!googleInitializedRef.current) {
      googleInitializedRef.current = true;
      g.accounts.id.initialize({
        client_id: clientId,
        itp_support: true,
        locale: 'he',
        auto_select: false,
        callback: async (response: any) => {
          const credential = response.credential;
          if (!credential) return;
  
          const result = await loginWithGoogleRef.current(credential);
          if (result.success) {
            navigate('/dashboard');
          } else if (result.needsPhone && result.pendingToken) {
            setPendingToken(result.pendingToken);
            setPhoneFirstName(result.profile?.first_name || '');
            setPhoneLastName(result.profile?.last_name || '');
            setFormError('');
          } else {
            setFormError(result.message || 'Google login failed');
          }
        },
      });
    }
  
    // const buttonParent = document.getElementById('googleSignInDiv');
    // if (!buttonParent) return;
  
    // const renderButton = (width: number) => {
    //   buttonParent.innerHTML = '';
    //   g.accounts.id.renderButton(buttonParent, {
    //     theme: 'filled_black',
    //     size: 'large',
    //     width,
    //     shape: 'rectangular',
    //     locale: 'he',
    //   });
    // };
  
    // // Use ResizeObserver to wait until the container has a real width
    // const observer = new ResizeObserver((entries) => {
    //   const w = entries[0].contentRect.width;
    //   if (w > 0) {
    //     observer.disconnect();
    //     renderButton(Math.floor(w));
    //   }
    // });
  
    // observer.observe(buttonParent);
  
    // // Fallback: if already has width on first tick, render immediately
    // if (buttonParent.offsetWidth > 0) {
    //   observer.disconnect();
    //   renderButton(buttonParent.offsetWidth);
    // }
  
    // return () => observer.disconnect();
  }, [pendingToken, showForgot, navigate]);
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      setFormError('Email and password are required');
      return;
    }
    setFormError('');

    const result = await login(formData.username, formData.password, rememberMe);
    if (result.success) {
      navigate('/dashboard');
    } else if (result.needsPhone && result.pendingToken) {
      setPendingToken(result.pendingToken);
      setPhoneFirstName(result.profile?.first_name || '');
      setPhoneLastName(result.profile?.last_name || '');
      setFormError('');
    } else {
      setFormError(result.message || 'Login failed');
    }
  };

  const handleCompletePhone = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!pendingToken || !phone) {
      setFormError('Phone number is required');
      return;
    }
    const res = await completePhone(pendingToken, phone, phoneFirstName, phoneLastName);
    if (res.success) {
      navigate('/dashboard');
    } else {
      setFormError(res.message || 'Failed to save phone');
    }
  };

  const handleForgotSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    setFormError('');
    setFormSuccess('');
    const res = await forgotPassword(forgotEmail);
    setForgotLoading(false);
    if (res.success) {
      setFormSuccess('אם המייל קיים במערכת, נשלח אליו קישור לאיפוס סיסמא.');
    } else {
      setFormError(res.message || 'Failed to send reset email');
    }
  };

  const inputClass =
    'appearance-none block w-full px-3 py-2 border border-stake-border rounded-md shadow-sm placeholder-gray-500 bg-dark-200 text-white focus:outline-none focus:ring-stake-blue focus:border-stake-blue sm:text-sm';

  const btnClass =
    'w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-stake-blue hover:bg-stake-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stake-blue';

  return (
    <div className="flex min-h-screen flex-col justify-center sm:px-6 lg:px-8 bg-dark-200">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <img src="/FIFA-2026-World-Cup-White-Logo.png" className="w-60 mx-auto" alt="logo" />
        <h2 className="text-center text-2xl font-extrabold text-white">מונדיאל 2026</h2>
        {/* <p className="mt-2 text-center text-sm text-gray-400">התחבר לחשבונך</p> */}
      </div>

      <div className="mt-4 rounded-lg shadow-md overflow-hidden min-w-80 sm:mx-auto sm:w-full sm:max-w-md m-2">
        <div className="bg-dark-300 py-4 px-4 shadow sm:rounded-lg sm:px-10">

          {/* ── Phone completion form ── */}
          {pendingToken ? (
            <form className="space-y-6" onSubmit={handleCompletePhone}>
              <p className="text-sm text-gray-400 text-center">
                כמעט סיימת! אשר את הפרטים והכנס מספר טלפון.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300">שם פרטי</label>
                  <div className="mt-1">
                    <input
                      type="text"
                      required
                      value={phoneFirstName}
                      onChange={(e) => setPhoneFirstName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">שם משפחה</label>
                  <div className="mt-1">
                    <input
                      type="text"
                      required
                      value={phoneLastName}
                      onChange={(e) => setPhoneLastName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                  מספר טלפון
                </label>
                <div className="mt-1">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              {(formError || error) && (
                <div className="text-stake-red text-sm">{formError || error}</div>
              )}
              <button type="submit" className={btnClass}>שמור והמשך</button>
            </form>

          /* ── Forgot password form ── */
          ) : showForgot ? (
            <form className="space-y-6" onSubmit={handleForgotSubmit}>
              <p className="text-sm text-gray-400 text-center">
                הזן את כתובת המייל שלך ונשלח לך קישור לאיפוס הסיסמא.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-300">כתובת מייל</label>
                <div className="mt-1">
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              {formError && <div className="text-stake-red text-sm">{formError}</div>}
              {formSuccess && <div className="text-green-400 text-sm">{formSuccess}</div>}
              <button type="submit" disabled={forgotLoading} className={btnClass}>
                {forgotLoading ? 'שולח...' : 'שלח קישור לאיפוס'}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setFormError(''); setFormSuccess(''); }}
                  className="text-sm text-stake-blue hover:text-stake-blue-light"
                >
                  חזרה להתחברות
                </button>
              </div>
            </form>

          /* ── Main login form ── */
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* <div id="googleSignInDiv" className="w-full flex justify-center h-[44px] min-h-[44px] overflow-hidden" />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-stake-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-dark-300 text-gray-400">או המשך עם מייל</span>
                </div>
              </div> */}

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                  כתובת מייל
                </label>
                <div className="mt-1">
                  <input
                    id="username"
                    name="username"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                    סיסמא
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setFormError(''); }}
                    className="text-xs text-stake-blue hover:text-stake-blue-light"
                  >
                    שכחתי סיסמא
                  </button>
                </div>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-stake-border bg-dark-200 text-stake-blue focus:ring-stake-blue"
                  />
                  <label htmlFor="rememberMe" className="mr-2 text-sm text-gray-300">
                    זכור אותי
                  </label>
                </div>
                {!pendingToken && signupEnabled && (
                  <Link to="/register" className="text-sm font-medium text-stake-blue hover:text-stake-blue-light">
                    צריך חשבון? הירשם
                  </Link>
                )}
              </div>

              {(formError || error) && (
                <div className="text-stake-red text-sm">{formError || error}</div>
              )}

              <button type="submit" className={btnClass}>התחבר</button>
            </form>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default Login;
